import React from 'react';
import { Icon } from '@/shared/icons';
import { Breadcrumb } from '@/shared/components/ui/Breadcrumb';
import { StatusPill } from '@/shared/components/ui/StatusPill';

/** Item da trilha de navegação (drill-down) do painel. `onClick` ausente = nível atual. */
export interface CustomizerBreadcrumbItem {
  label: string;
  onClick?: () => void;
}

interface CustomizerPanelProps {
  siteName: string;
  status: 'draft' | 'published' | 'archived';
  hasChanges: boolean;
  isSaving: boolean;
  isPublishing: boolean;
  /** Trilha do drill-down (root → nível atual). O último item é o atual (não clicável). */
  breadcrumb: CustomizerBreadcrumbItem[];
  depth: number;
  onExit: () => void;
  onSave: () => void;
  onPublish: () => void;
  onUnpublish: () => void;
  children: React.ReactNode;
}

/** Shell do painel drill-down: header (breadcrumb + site + ações) + trilha de nível + conteúdo. */
export const CustomizerPanel: React.FC<CustomizerPanelProps> = ({
  siteName, status, hasChanges, isSaving, isPublishing,
  breadcrumb, depth,
  onExit, onSave, onPublish, onUnpublish,
  children,
}) => {
  const isPublished = status === 'published';

  return (
    <div className="h-full flex flex-col">
      <style>{`@keyframes troiaPanelIn { from { opacity: 0; transform: translateX(12px); } to { opacity: 1; transform: translateX(0); } }`}</style>

      {/* Header — Zona 1: Breadcrumb shared (F25-F31) "Websites › {site}" + status */}
      <div className="flex-shrink-0 px-3 pt-3 pb-2.5 border-b" style={{ borderColor: 'var(--theme-card-border)' }}>
        <div className="flex items-center gap-1.5 mb-2.5 min-w-0">
          <Breadcrumb
            items={[
              { label: 'Websites', onNavigate: onExit, testid: 'customizer-exit-button' },
              { label: siteName, testid: 'customizer-site-name' },
            ]}
            mobileLegend="Websites"
            data-testid="customizer-header-breadcrumb"
          />
          <StatusPill
            variant={isPublished ? 'success' : status === 'archived' ? 'warning' : 'neutral'}
            label={isPublished ? 'Publicado' : status === 'archived' ? 'Arquivado' : 'Rascunho'}
            className="flex-shrink-0"
            data-testid="customizer-status-badge"
          />
        </div>

        {/* Zona 2: ações — Salvar (primário roxo, único) + Publicar/Despublicar */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onSave}
            disabled={!hasChanges || isSaving}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-opacity disabled:opacity-50"
            style={{ backgroundColor: 'var(--theme-button-primary)', color: 'var(--theme-button-primary-text)' }}
            data-testid="customizer-save-button"
          >
            <Icon name="save" className="w-4 h-4" />
            {isSaving ? 'Salvando...' : 'Salvar'}
          </button>

          {isPublished ? (
            <button
              type="button"
              onClick={onUnpublish}
              disabled={isPublishing}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold border transition-colors disabled:opacity-50"
              style={{ borderColor: 'var(--theme-card-border)', color: 'var(--theme-card-text)' }}
              data-testid="customizer-unpublish-button"
            >
              <Icon name="globe" className="w-4 h-4" />
              Despublicar
            </button>
          ) : (
            <button
              type="button"
              onClick={onPublish}
              disabled={isPublishing}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-opacity disabled:opacity-50"
              style={{ backgroundColor: '#10b981', color: '#ffffff' }}
              data-testid="customizer-publish-button"
            >
              <Icon name="globe" className="w-4 h-4" />
              {isPublishing ? 'Publicando...' : 'Publicar'}
            </button>
          )}
        </div>

        {hasChanges && (
          <p className="mt-2 text-[11px] flex items-center gap-1.5 font-medium" style={{ color: '#d97706' }} data-testid="customizer-unsaved-indicator">
            <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ backgroundColor: '#d97706' }} />
            Alterações não salvas
          </p>
        )}
      </div>

      {/* Zona 3: trilha do drill-down (breadcrumb — nunca seta de voltar). Oculta no root. */}
      {breadcrumb.length > 1 && (
        <nav
          className="flex-shrink-0 flex items-center flex-wrap gap-x-1.5 gap-y-1 px-3 py-2 border-b"
          style={{ borderColor: 'var(--theme-card-border)' }}
          data-testid="customizer-breadcrumb"
          aria-label="Breadcrumb"
        >
          {breadcrumb.map((crumb, i) => {
            const isLast = i === breadcrumb.length - 1;
            return (
              <React.Fragment key={`${i}-${crumb.label}`}>
                {i > 0 && (
                  /* Separador padrão: Icon chevron-right 14px, sem opacity extra (F29) */
                  <Icon
                    name="chevron-right"
                    className="w-3.5 h-3.5 flex-none"
                    style={{ color: 'var(--theme-muted-text)' }}
                  />
                )}
                {isLast || !crumb.onClick ? (
                  <span
                    className="text-sm font-semibold truncate max-w-[190px]"
                    style={{ color: 'var(--theme-card-text)' }}
                    aria-current={isLast ? 'page' : undefined}
                  >
                    {crumb.label}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={crumb.onClick}
                    className="text-sm font-medium rounded transition-colors truncate max-w-[160px]"
                    style={{ color: 'var(--theme-muted-text)' }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--theme-card-text)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--theme-muted-text)'; }}
                    data-testid={`customizer-breadcrumb-item-${i}`}
                  >
                    {crumb.label}
                  </button>
                )}
              </React.Fragment>
            );
          })}
        </nav>
      )}

      {/* Conteúdo do nível atual (key={depth} re-anima a cada navegação) — NÚCLEO, intocado */}
      <div key={depth} className="flex-1 overflow-y-auto px-3 py-3" style={{ animation: 'troiaPanelIn 0.15s ease' }}>
        {children}
      </div>
    </div>
  );
};
